/**
 * SKILLCARDS d'un élève (migration 0277) : celles qu'il a travaillées ∪ celles
 * qu'un tuteur lui a ajoutées « à travailler », avec sa progression.
 *
 * - Lecture : RPC `get_student_skillcards` (élève lui-même, tuteur lié, admin)
 *   — titre / rareté / couleur / créature du dernier instantané PUBLIÉ.
 * - Ajout / retrait par le tuteur : `card_assignments` en direct sous RLS, puis
 *   notification push à l'élève (`send-notification`, `card_assigned`).
 *
 * Statistiques et filtres : fonctions PURES, testées.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  CARD_COLOR_THEMES,
  CARD_DIFFICULTIES,
  CARD_IMPORTANCES,
  CARD_RARITIES,
  CARD_TYPES,
  defaultRarityFor,
  horaGain,
  horaStatus,
  type CardColorTheme,
  type HoraStatus,
  type CardRarity,
  type CardType,
} from '@jeraisonne/db/cards';

const db = supabase as unknown as SupabaseClient;

/** Au-delà, une carte est « maîtrisée » (maîtrise = réussite sur les 50 dernières questions). */
export const SKILLCARD_MASTERED_THRESHOLD = 80;

export interface SkillcardAssignment {
  id: string;
  assigned_at: string;
  note: string | null;
  tutor_id: string;
  tutor_name: string | null;
  tutor_avatar: string | null;
}

export interface StudentSkillcard {
  id: string;
  short_code: string;
  title: string;
  card_type: CardType;
  domain_label: string | null;
  rarity: CardRarity;
  color_theme: CardColorTheme;
  creature: unknown;
  levels: string[];
  xp_max: number;
  /** HORA COURANT (érodé) au moment de la lecture (migration 0315). */
  xp: number;
  /** HORA stocké au dernier crédit, et sa date : base du calcul de l'érosion. */
  xp_stored: number;
  xp_updated_at: string | null;
  mastery: number;
  usage_count: number;
  last_used_at: string | null;
  /** Carte achetée (carte / pack / programme, deck actif — migration 0309). */
  owned: boolean;
  /** Ratée au dernier diagnostic de programme (migration 0318) : à travailler. */
  diagnostic_flag: boolean;
  assignment: SkillcardAssignment | null;
}

const oneOf = <T extends string>(list: readonly T[], v: unknown, fallback: T): T =>
  (list as readonly string[]).includes(v as string) ? (v as T) : fallback;

/** Ligne brute de la RPC → carte normalisée (repli pour un instantané ancien). */
export function normalizeStudentSkillcard(raw: Record<string, unknown>): StudentSkillcard {
  const importance = oneOf(CARD_IMPORTANCES, raw.importance, 'importante');
  const difficulty = oneOf(CARD_DIFFICULTIES, raw.difficulty, 'intermediaire');
  const num = (v: unknown, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  return {
    id: String(raw.id),
    short_code: String(raw.short_code ?? ''),
    title: String(raw.title ?? 'SKILLCARD'),
    card_type: oneOf(CARD_TYPES, raw.card_type, 'methode'),
    domain_label:
      typeof raw.domain_label === 'string' && raw.domain_label ? raw.domain_label : null,
    rarity: oneOf(CARD_RARITIES, raw.rarity, defaultRarityFor(importance, difficulty)),
    color_theme: oneOf(CARD_COLOR_THEMES, raw.color_theme, 'level'),
    creature: raw.creature ?? null,
    levels: Array.isArray(raw.levels) ? (raw.levels as string[]) : [],
    xp_max: num(raw.xp_max, 1500),
    xp: num(raw.xp),
    xp_stored: num(raw.xp_stored, num(raw.xp)),
    xp_updated_at: typeof raw.xp_updated_at === 'string' ? raw.xp_updated_at : null,
    mastery: num(raw.mastery),
    usage_count: num(raw.usage_count),
    last_used_at: typeof raw.last_used_at === 'string' ? raw.last_used_at : null,
    owned: raw.owned === true,
    diagnostic_flag: raw.diagnostic_flag === true,
    assignment: (raw.assignment as SkillcardAssignment | null) ?? null,
  };
}

/** Catalogue public des SKILLCARDS (site) : d'où l'on en ajoute à sa collection. */
export function skillcardCatalogUrl(origin?: string): string {
  return `${origin ?? 'https://www.jeraisonne.fr'}/skillcards`;
}

export const isSkillcardMastered = (c: Pick<StudentSkillcard, 'mastery'>) =>
  c.mastery >= SKILLCARD_MASTERED_THRESHOLD;

/**
 * « À travailler » : ajoutée par un tuteur, ou ratée au dernier diagnostic de
 * programme (0318), et pas encore maîtrisée.
 */
export const isSkillcardToWork = (
  c: Pick<StudentSkillcard, 'mastery' | 'assignment'> & { diagnostic_flag?: boolean },
) => (!!c.assignment || !!c.diagnostic_flag) && !isSkillcardMastered(c);

export interface SkillcardStats {
  count: number;
  totalXp: number;
  totalXpMax: number;
  /** Moyenne des maîtrises des cartes déjà travaillées (null si aucune). */
  averageMastery: number | null;
  mastered: number;
  toWork: number;
}

export function skillcardStats(cards: readonly StudentSkillcard[]): SkillcardStats {
  const worked = cards.filter((c) => c.usage_count > 0);
  return {
    count: cards.length,
    totalXp: cards.reduce((a, c) => a + c.xp, 0),
    totalXpMax: cards.reduce((a, c) => a + c.xp_max, 0),
    averageMastery: worked.length
      ? Math.round(worked.reduce((a, c) => a + c.mastery, 0) / worked.length)
      : null,
    mastered: cards.filter(isSkillcardMastered).length,
    toWork: cards.filter(isSkillcardToWork).length,
  };
}

/** HORA vivant d'une carte (érosion recalculée à l'instant `now`). */
export function skillcardHora(
  c: Pick<StudentSkillcard, 'xp_stored' | 'mastery' | 'xp_updated_at'>,
  now: Date = new Date(),
): HoraStatus {
  return horaStatus(c.xp_stored, c.mastery, c.xp_updated_at, now);
}

/** Seuil de perte (HORA) pour qu'une carte soit « à retravailler ». */
export const SKILLCARD_REWORK_MIN_LOST = 1;

/** « À retravailler » : a perdu de l'HORA depuis le dernier exercice. */
export const isSkillcardToRework = (
  c: Pick<StudentSkillcard, 'xp_stored' | 'mastery' | 'xp_updated_at'>,
  now: Date = new Date(),
) => skillcardHora(c, now).lost >= SKILLCARD_REWORK_MIN_LOST;

/** Cartes qui ont le plus perdu (HORA absolu, puis part perdue), d'abord. */
export function skillcardsToRework(
  cards: readonly StudentSkillcard[],
  limit = Infinity,
  now: Date = new Date(),
): StudentSkillcard[] {
  return cards
    .map((c) => ({ c, h: skillcardHora(c, now) }))
    .filter(({ h }) => h.lost >= SKILLCARD_REWORK_MIN_LOST)
    .sort((a, b) => b.h.lost - a.h.lost || b.h.lostPct - a.h.lostPct)
    .slice(0, limit)
    .map(({ c }) => c);
}

export type SkillcardFilter = 'all' | 'to_work' | 'to_rework' | 'mastered';

export function filterSkillcards(
  cards: readonly StudentSkillcard[],
  filter: SkillcardFilter,
  now: Date = new Date(),
): StudentSkillcard[] {
  if (filter === 'to_rework') return skillcardsToRework(cards, Infinity, now);
  if (filter === 'to_work') return cards.filter(isSkillcardToWork);
  if (filter === 'mastered') return cards.filter(isSkillcardMastered);
  return [...cards];
}

/** Date d'activité la plus récente (travail ou ajout). */
export function skillcardActivityAt(c: StudentSkillcard): string {
  const dates = [c.last_used_at, c.assignment?.assigned_at].filter((d): d is string => !!d);
  return dates.sort().at(-1) ?? '';
}

/** Les plus récentes d'abord : les cartes à travailler remontent en tête. */
export function recentSkillcards(cards: readonly StudentSkillcard[], limit: number) {
  return [...cards]
    .sort(
      (a, b) =>
        Number(isSkillcardToWork(b)) - Number(isSkillcardToWork(a)) ||
        skillcardActivityAt(b).localeCompare(skillcardActivityAt(a)),
    )
    .slice(0, limit);
}

// ── Hooks ────────────────────────────────────────────────────────────────────

export const studentSkillcardsKey = (studentId: string) =>
  ['student-skillcards', studentId] as const;

export function useStudentSkillcards(studentId: string | null | undefined) {
  return useQuery({
    queryKey: studentSkillcardsKey(studentId ?? ''),
    enabled: !!studentId,
    staleTime: 30_000,
    queryFn: async (): Promise<StudentSkillcard[]> => {
      const { data, error } = await db.rpc('get_student_skillcards', {
        p_student_id: studentId,
      });
      if (error) throw new Error(error.message);
      return ((data ?? []) as Record<string, unknown>[]).map(normalizeStudentSkillcard);
    },
  });
}

/** Le tuteur ajoute une SKILLCARD à son élève, puis l'élève est notifié (push). */
export function useAssignSkillcard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      cardId: string;
      studentId: string;
      tutorId: string;
      note?: string;
    }) => {
      const { data, error } = await db
        .from('card_assignments')
        .insert({
          card_id: input.cardId,
          student_id: input.studentId,
          tutor_id: input.tutorId,
          note: input.note?.trim() || null,
        })
        .select('id')
        .single();
      if (error) {
        if (error.code === '23505')
          throw new Error('Cette SKILLCARD est déjà ajoutée à cet élève.');
        throw new Error(
          error.message.includes('row-level security')
            ? 'Ajout impossible : la carte doit être publiée, de son niveau, et l’élève lié à toi.'
            : error.message,
        );
      }
      const id = (data as { id: string }).id;
      // Notification : best-effort, l'ajout reste valable si le push échoue.
      void supabase.functions
        .invoke('send-notification', { body: { type: 'card_assigned', assignment_id: id } })
        .catch(() => undefined);
      return id;
    },
    onSettled: (_d, _e, vars) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: studentSkillcardsKey(vars.studentId) }),
        qc.invalidateQueries({ queryKey: ['card-catalog', vars.studentId] }),
      ]),
  });
}

export function useUnassignSkillcard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { assignmentId: string; studentId: string }) => {
      const { error } = await db.from('card_assignments').delete().eq('id', input.assignmentId);
      if (error) throw new Error(error.message);
    },
    onSettled: (_d, _e, vars) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: studentSkillcardsKey(vars.studentId) }),
        qc.invalidateQueries({ queryKey: ['card-catalog', vars.studentId] }),
      ]),
  });
}

// ── Cartes reliées aux questions d'un exercice (popup « Bonne réponse ») ─────

export interface ExerciseCardLink {
  /** `statement_question` (V2) ou `exercise_steps` (V1) reliée à la carte. */
  question_id: string;
  card_id: string;
  title: string;
  rarity: CardRarity;
  color_theme: CardColorTheme;
  creature: unknown;
  levels: string[];
  xp_max: number;
  question_xp: number;
  /** Difficulté de la question : ×1 / ×1,5 / ×2 (migration 0280). */
  difficulty_mult: number;
  /** Bonus de maîtrise × frein « exercice refait » de l'élève (migration 0315). */
  gain_factor: number;
}

/** HORA d'une bonne réponse à CETTE question pour la carte (barème 0315). */
export function linkQuestionXp(
  l: Pick<ExerciseCardLink, 'question_xp' | 'difficulty_mult'> & { gain_factor?: number },
) {
  return horaGain({
    questionXp: l.question_xp,
    difficultyMult: l.difficulty_mult,
    gainFactor: l.gain_factor ?? 1,
  });
}

export function useExerciseCardLinks(exerciseId: string, enabled = true) {
  return useQuery({
    queryKey: ['exercise-card-links', exerciseId],
    enabled: enabled && !!exerciseId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ExerciseCardLink[]> => {
      const { data, error } = await db.rpc('get_exercise_card_links', {
        p_exercise_id: exerciseId,
      });
      if (error) throw new Error(error.message);
      return ((data ?? []) as Record<string, unknown>[]).map((raw) => {
        const c = normalizeStudentSkillcard({ ...raw, id: raw.card_id });
        return {
          question_id: String(raw.question_id),
          card_id: c.id,
          title: c.title,
          rarity: c.rarity,
          color_theme: c.color_theme,
          creature: c.creature,
          levels: c.levels,
          xp_max: c.xp_max,
          question_xp: typeof raw.question_xp === 'number' ? raw.question_xp : 1,
          difficulty_mult: raw.difficulty_mult == null ? 1 : Number(raw.difficulty_mult) || 1,
          gain_factor: raw.gain_factor == null ? 1 : Number(raw.gain_factor) || 1,
        };
      });
    },
  });
}

/**
 * Carte principale d'un exercice : celle reliée au plus grand nombre de ses
 * questions (à égalité, la première rencontrée). Sert de cover de repli.
 */
export function mainCardOfLinks(links: readonly ExerciseCardLink[]): ExerciseCardLink | null {
  const counts = new Map<string, { link: ExerciseCardLink; n: number }>();
  for (const l of links) {
    const entry = counts.get(l.card_id);
    if (entry) entry.n += 1;
    else counts.set(l.card_id, { link: l, n: 1 });
  }
  let best: { link: ExerciseCardLink; n: number } | null = null;
  for (const entry of counts.values()) if (!best || entry.n > best.n) best = entry;
  return best?.link ?? null;
}

/** Cartes que crédite une bonne réponse à cette question (une fois par carte). */
export function cardsForQuestion(
  links: readonly ExerciseCardLink[],
  questionId: string | null | undefined,
): ExerciseCardLink[] {
  if (!questionId) return [];
  const seen = new Set<string>();
  return links.filter((l) => {
    if (l.question_id !== questionId || seen.has(l.card_id)) return false;
    seen.add(l.card_id);
    return true;
  });
}

export interface CardXpGainItem {
  card: ExerciseCardLink;
  /** XP gagnée (ou à gagner) par la carte, plafonnée à ce qui manque. */
  gain: number;
  /** La carte était déjà au maximum. */
  complete: boolean;
  /** Part venue de la feuille de brouillon (migration 0369), comprise dans `gain`. */
  fromScratch?: number;
}

/**
 * XP gagnée par chaque carte sur une tentative (bilan de fin d'exercice) :
 * chaque bonne réponse à une question reliée vaut `linkQuestionXp` (barème
 * 0315 : difficulté × maîtrise × frein « refait »), plafonné à
 * `xp_max − HORA actuel` — MIROIR de `credit_card_xp_for_attempt`.
 * `correctQuestionIds` : une entrée par réponse juste (une question
 * pas-à-pas peut compter plusieurs fois). Cartes sans bonne réponse omises.
 * `scratchByQuestion` : HORA de la feuille de brouillon des questions répondues
 * (justes ou non), qui vont à la carte de la question — miroir de
 * `credit_scratch_hora_for_attempt` (0369), hors frein « refait » (serveur).
 */
export function cardXpGains(
  links: readonly ExerciseCardLink[],
  correctQuestionIds: readonly string[],
  progress: readonly Pick<StudentSkillcard, 'id' | 'xp'>[] = [],
  scratchByQuestion: Readonly<Record<string, number>> = {},
): CardXpGainItem[] {
  // Par carte : XP de chaque question reliée (difficulté comprise).
  const byCard = new Map<string, { card: ExerciseCardLink; xpByQuestion: Map<string, number> }>();
  for (const l of links) {
    const entry = byCard.get(l.card_id) ?? { card: l, xpByQuestion: new Map<string, number>() };
    entry.xpByQuestion.set(l.question_id, linkQuestionXp(l));
    byCard.set(l.card_id, entry);
  }
  const items: CardXpGainItem[] = [];
  for (const { card, xpByQuestion } of byCard.values()) {
    const earned = correctQuestionIds.reduce((sum, q) => sum + (xpByQuestion.get(q) ?? 0), 0);
    const scratch = [...xpByQuestion.keys()].reduce(
      (sum, q) => sum + Math.max(0, scratchByQuestion[q] ?? 0),
      0,
    );
    if (!correctQuestionIds.some((q) => xpByQuestion.has(q)) && scratch === 0) continue;
    const current = progress.find((p) => p.id === card.card_id)?.xp ?? 0;
    const remaining = Math.max(0, card.xp_max - current);
    const gain = Math.min(remaining, earned + scratch);
    items.push({
      card,
      gain,
      complete: remaining === 0,
      ...(scratch > 0 ? { fromScratch: Math.min(scratch, gain) } : {}),
    });
  }
  return items;
}
